/**
 * Derived-topology hooks — the display side of the one policy.
 *
 * Grid-only by owner ruling (2026-09-02): both hooks return null unless the
 * derive answered from a module cell. No proximity guesses reach any surface.
 * Memoized on the inputs' reference identity (c6 contract: the chip runs a
 * derive per displayed indication — never per render).
 */

import { useMemo } from 'react';
import { useProjectStore, type Indication } from '../../stores/projectStore';
import { planTopologyUpdate, type TopologyApplyPlan } from './applyDerivedTopology';
import { deriveTopology, type GridDeriveResult } from './deriveTopology';
import type { SiteLayout, TopologyFields } from '../layout/types';

function useAllLayouts(): SiteLayout[] {
  const siteLayouts = useProjectStore((s) => s.data.siteLayouts);
  const alias = useProjectStore((s) => s.data.siteLayout);
  return useMemo(
    () => siteLayouts ?? (alias ? [alias] : []),
    [siteLayouts, alias],
  );
}

/**
 * Full write-plan for an EXISTING indication (edit panel, preview chip):
 * fills, conflicts vs stored values, provenance-carrying updates. Null when
 * no layouts, or the derive found no grid cell (owner ruling — no guesses).
 * respectHuman: chip surfaces never re-litigate a value the user defended
 * ("Keep stored") or typed — those fields simply drop out of the plan.
 */
export function useDerivedTopologyPlan(
  indication: Indication | undefined | null,
): TopologyApplyPlan | null {
  const layouts = useAllLayouts();
  return useMemo(() => {
    if (!indication || layouts.length === 0) return null;
    const plan = planTopologyUpdate(indication, layouts, { respectHuman: true });
    if (!plan || plan.result.kind !== 'grid') return null;
    return plan;
    // geometry/properties are replaced (not mutated) on change — reference
    // identity is the correct dependency
  }, [indication, indication?.geometry, indication?.properties, indication?.type, layouts]);
}

/**
 * Grid derivation for an arbitrary GEOMETRY that is not (yet) an indication
 * — the add popup's would-be rect, a classify-panel marker. Same
 * grid-or-nothing contract.
 */
export function useDerivedFieldsForGeometry(
  geometry: GeoJSON.Geometry | null,
  indicationType?: string,
): { fields: Partial<TopologyFields>; customFields?: Record<string, string>; result: GridDeriveResult } | null {
  const layouts = useAllLayouts();
  return useMemo(() => {
    if (!geometry || layouts.length === 0) return null;
    const result = deriveTopology(layouts, geometry, { indicationType });
    if (result.kind !== 'grid') return null;
    return { fields: result.fields, customFields: result.customFields, result };
  }, [geometry, indicationType, layouts]);
}

/**
 * Grid derivation at a CLICK POINT — fallback for callers with no geometry
 * to offer. Prefer useDerivedFieldsForGeometry with the rect that WILL
 * exist: the quick-add path derives on the final rect, and two add surfaces
 * must not run two engines (c6 review of 782dbcc).
 */
export function useDerivedFieldsAt(
  lngLat: [number, number] | null,
): { fields: Partial<TopologyFields>; customFields?: Record<string, string>; result: GridDeriveResult } | null {
  const geometry = useMemo(
    () => (lngLat ? ({ type: 'Point', coordinates: lngLat } as GeoJSON.Geometry) : null),
    [lngLat?.[0], lngLat?.[1]],
  );
  return useDerivedFieldsForGeometry(geometry);
}
